export const FEATURES = {
  ARTICLES_ENABLED: true,
  /** Invoices page and nav for curator and admin routes. */
  INVOICES_ENABLED: true,
  PLAYLIST_JINGLE:
    (process.env.REACT_APP_ENV || process.env.NODE_ENV) !== "production", // per-playlist jingle selector
  /**
   * Social Media Promo page (`/social-media-promo`) and its nav entry, split by
   * audience so it can ship to admins while curators wait [ROVR-3406].
   *
   * `_ADMIN` covers admin and manager, `_CURATOR` covers curators (subcurators
   * included). The route is only mounted when at least one of the two is on, and
   * `ProtectedRoute` narrows it to exactly the roles whose flag is on — a curator
   * opening the URL directly while `_CURATOR` is off is redirected to `/`.
   *
   * `_ADMIN` is on everywhere as of 1.9.6: QA signed the page off on devdash and
   * admins need it in production. `_CURATOR` stays off — it has never run in any
   * environment, so nobody has tested the curator side yet.
   */
  SOCIAL_MEDIA_PROMO_ADMIN_ENABLED: true,
  SOCIAL_MEDIA_PROMO_CURATOR_ENABLED: false,
  /**
   * Curator messaging (IVS chat): /messages route, top-bar icon, and polling.
   *
   * On everywhere now. The top-bar icon and the conversation polling both key
   * off the `/messages` route being present in `appRoutes`, so this stays the
   * single switch for the whole feature.
   */
  MESSAGING_ENABLED: true,
  /**
   * Admin-only clients CSV export page and nav.
   *
   * On everywhere, production included — enabled there by `ca7e261`.
   */
  CLIENTS_EXPORT_ENABLED: true,
  /** Admin-only "Search Track Details" (Shazam) action in the playlist track list. */
  SEARCH_TRACK_DETAILS_ENABLED: true,
  /** Admin/manager-only "Change to Ready" action for submitted playlist rows. */
  CHANGE_TO_READY_ENABLED: true,
  /** Admin-only schedule board: drag shows onto a 7-day x 48-week grid. */
  SCHEDULE_BOARD_ENABLED: true,
  /**
   * Admin-only schedule board persistence: "Save" the board draft to a shared
   * server-side store and "Pull master schedule" to seed the editable board.
   *
   * On everywhere, production included — enabled there by `be88e22`. Both
   * actions read and write `GET`/`PUT /api/schedule-board`, granted to `admin`
   * only in the Strapi permissions config, so that backend must stay live in
   * production. Draft-only: neither action writes the live `schedule`
   * collection.
   */
  SCHEDULE_BOARD_PERSIST_ENABLED: true,
  /**
   * Admin-only "Apply" action on the schedule board comparison view: replace the
   * master-schedule slot's upcoming event with the board-side show. Off in
   * production until the flow is verified against live schedule data.
   */
  SCHEDULE_BOARD_APPLY_ENABLED:
    (process.env.REACT_APP_ENV || process.env.NODE_ENV) !== "production",
  SCHEDULE_CALENDAR_WEEKS: 8,
  /** Library-style track filters (title, artist, BPM, missing platform, etc.) in the Mood +Add panel. */
  MOOD_ADD_LIBRARY_FILTERS_ENABLED:
    (process.env.REACT_APP_ENV || process.env.NODE_ENV) !== "production",
  /**
   * Admin-only "Scheduled Date Range" filter in the Library drawer: filters tracks by their
   * playlist's scheduleDate. Pairs with "Missing Shazam" to surface upcoming-week tracks still
   * missing enrichment. Off in production until verified against live schedule data.
   */
  LIBRARY_SCHEDULE_DATE_FILTER_ENABLED:
    (process.env.REACT_APP_ENV || process.env.NODE_ENV) !== "production",
  /**
   * Render the mix player from the compact `.wab` binary waveform instead of the
   * waveform PNG, and forward the extracted waveform urls on track creation
   * [ROVR-3826 / ROVR-3829].
   *
   * On everywhere now. A playlist without a `.wab` — every one processed before
   * ROVR-3809, and all of production until its worker's CLI build is updated —
   * keeps rendering the image, so this is inert rather than broken wherever the
   * binary is not available yet.
   */
  WAVEFORM_BINARY_ENABLED: true,
  /**
   * Admin-only `/status-check` page: operational counts read straight from the
   * backend. Ships with the ticket breakdown by `ticketStatus`.
   *
   * On everywhere, production included. The page reads
   * `GET /api/tickets/status-counts`, granted to `admin` in the Strapi
   * permissions config — so the backend must be live in production before or
   * alongside this, or a prod admin sees a 403/404 in the breakdown panel.
   */
  STATUS_CHECK_ENABLED: true,
  /**
   * Subtags section in the submit-playlist tags popup: picking a tag reveals its
   * subtags, of which the curator may pick any number [ROVR-3982, ROVR-4246].
   *
   * On everywhere, production included. The flag guards the `subPlaylistTags`
   * populate and write as much as the UI — Strapi rejects a populate key that
   * has no matching relation with a 400, which takes the whole tag list down
   * with it. `playlist-tag` and `playlist` carry the relation on both devstrapi
   * and production Strapi.
   */
  PLAYLIST_SUBTAGS_ENABLED: true,
  /**
   * Admin/manager-only `/admin-messages` page: read every curator↔listener
   * conversation and its full transcript, read-only.
   *
   * On everywhere, production included — enabled there by `5d7fdbf`. The page
   * reads `GET /api/curator-conversations/admin/conversations`
   * (+ `/:id/history`), granted to `admin` and `manager` in the Strapi
   * permissions config, so that backend must stay live in production.
   */
  ADMIN_CURATOR_MESSAGES_ENABLED: true,
  /**
   * Admin/curator-only "change track image" on the playlist track list
   * (ROVR-4157). Uploads to S3 and sends `coverUrl` on the track PUT, which
   * Strapi attaches to the `cover` media field and mirrors onto
   * `albumCoverUrl`/`fileAlbumArt`. `cover`/`coverUrl` only exist on devstrapi
   * so far, and the tracks fetch asks for `coverUrl` under this flag, so flip
   * production on once the field is deployed there.
   */
  TRACK_COVER_OVERRIDE_ENABLED:
    (process.env.REACT_APP_ENV || process.env.NODE_ENV) !== "production",
  /**
   * Two optional "extra" slots beside the Vinyl List top 5 [ROVR-4187]. The
   * extras are submitted as `shop-record` rows flagged `extra: true`, so they
   * ride along with the selection without ever counting as part of the top 5.
   *
   * On everywhere, production included. Strapi's half (`a20afacf`: the `extra`
   * attribute, 5 top + up to 2 extras accepted, extras stripped from
   * `/shop-curators/public`) is on both devstrapi and production Strapi, so
   * production Strapi must keep it or a submit that carries extras would 400.
   */
  VINYL_EXTRA_SELECTIONS_ENABLED: true,
  /**
   * Read each manager's entitlements from `GET /api/managers/me/configurations`
   * (Admin / Manager / Manager-View-Only), so downloads and the "All Messages"
   * tab can be switched off per manager — see `useManagerEntitlement`.
   *
   * On everywhere, production included. It is inert until an admin creates a
   * `manager` row in Strapi: a manager without one gets every entitlement, and
   * while production Strapi has no such endpoint the 404 falls back to today's
   * access (downloads with the daily limit, All Messages). So the dash and
   * Strapi can be released in either order.
   */
  MANAGER_CONFIGURATIONS_ENABLED: true,
} as const;
